import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import { useDataProvider } from 'react-admin';

import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import classNames from 'classnames';

import { BillDialog } from 'components/common';

import { httpClient } from 'services/data';

import useCustomNotify from 'hooks/useCustomNotify';

import Card from './components/payment-flows/Card';
import ACH from './components/payment-flows/ACH';
import { removePaymentAmount } from 'store/actions/refillActions';
import { getDomesticCountryCode } from 'services/helpers/countryCode';
import AddBankAccount from './components/AddBankAccount';
import { ReactComponent as BankIcon } from 'assets/general/icons/Bank.svg';
import { ReactComponent as SecureIcon } from 'assets/general/icons/SecureOutlined.svg';
import { ReactComponent as FeesIcon } from 'assets/general/icons/FeeOutlined.svg';
import { ReactComponent as StarIcon } from 'assets/general/icons/StarOutlined.svg';
import PaymentMethodsErrorDialog from '../PaymentMethodsErrorDialog';

const useStyles = makeStyles((theme) => ({
  processingPaymentLoader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  processingPaymentText: {
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  paymentTypesContainer: {
    display: 'flex',
    gap: '8px',
    padding: '24px 16px',
  },
  paymentType: {
    width: '50%',
    borderRadius: '16px',
    border: '1px solid #E1E1E1',
    padding: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer',
    color: '#969AB3',
  },
  paymentTypeInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'space-between',
  },
  activePaymentType: {
    color: '#6C13F9',
    borderColor: '#6C13F9',
  },
  dialogBody: {
    maxWidth: '560px',
    margin: '0 auto',
  },
  chip: {
    height: '28px',
    maxWidth: '115px',
    backgroundColor: 'rgba(150, 154, 178, 0.1)',
    fontSize: '14px',
    fontWeight: 800,
    gap: '4px',
    color: theme.palette.secondary.contrastText,
    padding: '0 8px',
    '& .MuiChip-label': {
      padding: 0,
      margin: 0,
    },
    '& .MuiChip-icon': {
      padding: 0,
      margin: 0,
      color: theme.palette.secondary.contrastText,
    },
  },
  chipContainer: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  chipLine: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  activeAchChip: {
    backgroundColor: '#DFFFE6',
    color: '#45D264',
    '& .MuiChip-icon': {
      color: '#45D264',
    },
  },
  activeCardChip: {
    backgroundColor: 'rgba(251, 157, 45, 0.13);',
    color: '#FF9900',
    '& .MuiChip-icon': {
      color: '#FF9900',
    },
  },
}));

const MANAGE_PAGE = 'Manage';
const ADD_BALANCE = 'Add balance';
const ADD_ACCOUNT = 'Add new account';

const stripePromise = async (notify) => {
  try {
    const apiUrl = `${process.env.REACT_APP_DATA_PROVIDER_URL}/payments/settings`;
    const options = {
      method: 'GET',
    };
    const { json } = await httpClient(apiUrl, options);

    return loadStripe(json.stripePublishableKey as string, { apiVersion: '2022-08-01' });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load Stripe';
    notify(errorMessage);
    return null;
  }
};

const PaymentLoader = () => {
  const classes = useStyles();
  return (
    <div className={classes.processingPaymentLoader}>
      <CircularProgress size={24} />
      <Typography className={classes.processingPaymentText}>Processing payment</Typography>
    </div>
  );
};

const RefillDialog = (props) => {
  const notify = useCustomNotify();
  const classes = useStyles();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();

  const domesticCountryCode = getDomesticCountryCode();

  const [paymentOptions, setPaymentOptions] = useState<any>(undefined);
  const [paymentOptionsLoading, setPaymentOptionsLoading] = useState<boolean>(false);
  const [paymentLimits, setPaymentLimits] = useState<any>(undefined);
  const [amount, setAmount] = useState<number>(0);
  const [currentAutoRefillStatus, setCurrentAutoRefillStatus] = useState<boolean>(false);
  const [pageTitle, setPageTitle] = useState<string>(MANAGE_PAGE);
  const [paymentType, setPaymentType] = useState<'ACH' | 'CARD'>(
    domesticCountryCode === 'US' ? 'ACH' : 'CARD'
  );
  const [errorAmount, setErrorAmount] = useState<number>(0);
  const [cardEnabled, setCardEnabled] = useState<boolean>(true);
  const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);

  useEffect(() => {
    const getPaymentOptions = async () => {
      try {
        setPaymentOptionsLoading(true);
        const { data } = await dataProvider.getList('paymentOptions');

        setPaymentOptions(data);
      } catch (error: any) {
        console.error(error?.message ?? error);
      } finally {
        setPaymentOptionsLoading(false);
      }
    };

    getPaymentOptions();
  }, [dataProvider]);

  useEffect(() => {
    const getPaymentLimits = async () => {
      try {
        const { data } = await dataProvider.get('paymentLimits');

        setPaymentLimits(data);
      } catch (error: any) {
        console.error(error?.message ?? error);
      }
    };

    getPaymentLimits();
  }, [dataProvider]);

  const getBillingData = async () => {
    try {
      const { data: billingData } = await dataProvider.get('billingSettings');

      setCardEnabled(billingData?.creditCardPaymentAllowed);

      if (!billingData?.creditCardPaymentAllowed && domesticCountryCode === 'CA') {
        setOpenErrorDialog(true);
        return;
      }

      if (!billingData?.creditCardPaymentAllowed && paymentType === 'CARD') {
        setPaymentType('ACH');
      }

      return billingData;
    } catch (error) {
      throw error;
    }
  };

  const callback = useSelector((state) => state.refill.callback, shallowEqual);
  const singlePaymentAmount = useSelector(
    (state) => state.refill.singlePaymentAmount,
    shallowEqual
  );
  const stripe = useMemo(() => stripePromise(notify), [notify]);

  useEffect(() => {
    if (singlePaymentAmount) {
      setErrorAmount(singlePaymentAmount);

      dispatch(removePaymentAmount());
    }
  }, [dispatch, singlePaymentAmount, setErrorAmount]);

  const handleChangeType = (value) => {
    setPaymentType(value);
  };

  const cancelBankAccount = () => {
    setPageTitle(MANAGE_PAGE);
  };

  const handleAccountConnect = () => {
    setCurrentAutoRefillStatus(true);
    setPageTitle(MANAGE_PAGE);
  };

  const isAch = paymentType === 'ACH';
  const isCard = paymentType === 'CARD';

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
    callback();
  };

  if (openErrorDialog) {
    return <PaymentMethodsErrorDialog open={openErrorDialog} onClose={handleCloseErrorDialog} />;
  }

  const handleCloseDialog = () => {
    callback();
  };

  return (
    <BillDialog
      open={true}
      title={pageTitle}
      dialogProps={{ disableEnforceFocus: isAch }}
      onCancel={handleCloseDialog}>
      {domesticCountryCode === 'US' && pageTitle === MANAGE_PAGE && cardEnabled && (
        <Box className={classes.paymentTypesContainer}>
          <Box
            onClick={() => handleChangeType('ACH')}
            className={classNames(classes.paymentType, isAch && classes.activePaymentType)}>
            <Box className={classes.paymentTypeInner}>
              <BankIcon />
              <Typography>Bank</Typography>
            </Box>

            <Box className={classes.chipContainer}>
              <Chip
                className={classNames(classes.chip, isAch && classes.activeAchChip)}
                icon={<StarIcon />}
                label="3x Rewards"
              />

              <Box className={classes.chipLine}>
                <Chip
                  className={classNames(classes.chip, isAch && classes.activeAchChip)}
                  icon={<FeesIcon />}
                  label="No Fees"
                />
                <Chip
                  className={classNames(classes.chip, isAch && classes.activeAchChip)}
                  icon={<SecureIcon />}
                  label="Secure"
                />
              </Box>
            </Box>
          </Box>

          <Box
            onClick={() => handleChangeType('CARD')}
            className={classNames(classes.paymentType, isCard && classes.activePaymentType)}>
            <Box className={classes.paymentTypeInner}>
              <CreditCardIcon />
              <Typography>Card</Typography>
            </Box>

            <Box className={classes.chipContainer}>
              <Chip
                className={classNames(classes.chip, isCard && classes.activeCardChip)}
                icon={<StarIcon />}
                label="1x Rewards"
              />
              <Chip
                className={classNames(classes.chip, isCard && classes.activeCardChip)}
                icon={<FeesIcon />}
                label="Higher Fees"
              />
            </Box>
          </Box>
        </Box>
      )}

      {paymentType === 'CARD' && (
        <Elements stripe={stripe}>
          <Card
            setPageTitle={setPageTitle}
            callback={callback}
            paymentOptions={paymentOptions}
            paymentOptionsLoading={paymentOptionsLoading}
            paymentLimits={paymentLimits}
            paymentLoader={<PaymentLoader />}
            errorAmount={errorAmount}
            setErrorAmount={setErrorAmount}
            getBillingData={getBillingData}
          />
        </Elements>
      )}

      {paymentType === 'ACH' && pageTitle === ADD_BALANCE && !!amount && (
        <Box className={classes.dialogBody}>
          <AddBankAccount
            onCancel={cancelBankAccount}
            amount={amount}
            paymentLoader={<PaymentLoader />}
            onClose={callback}
            pageTitle={pageTitle}
            stripe={stripe}
            onAccountConnect={handleAccountConnect}
          />
        </Box>
      )}

      {paymentType === 'ACH' && pageTitle === ADD_ACCOUNT && (
        <Box className={classes.dialogBody}>
          <AddBankAccount
            onCancel={cancelBankAccount}
            paymentLoader={<PaymentLoader />}
            onClose={callback}
            pageTitle={pageTitle}
            stripe={stripe}
            onAccountConnect={handleAccountConnect}
          />
        </Box>
      )}

      {paymentType === 'ACH' && (
        <ACH
          pageTitle={pageTitle}
          setPageTitle={setPageTitle}
          callback={callback}
          paymentOptions={paymentOptions}
          paymentOptionsLoading={paymentOptionsLoading}
          paymentLimits={paymentLimits}
          paymentLoader={<PaymentLoader />}
          stripePromise={stripe}
          getBillingData={getBillingData}
          amount={amount}
          setAmount={setAmount}
          currentAutoRefillStatus={currentAutoRefillStatus}
          errorAmount={errorAmount}
          setErrorAmount={setErrorAmount}
        />
      )}
    </BillDialog>
  );
};

export default RefillDialog;
